import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Send, MapPin } from "lucide-react";
import { toast } from "sonner";
import { suggestRoute } from "@/lib/api";

/**
 * Modal para sugestão de rota pela comunidade.
 * Não redireciona — abre em overlay, envia e fecha.
 */
export default function SuggestRouteModal({ open, onClose }) {
  const [f, setF] = useState({ name: "", email: "", route_name: "", city_state: "", description: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!f.route_name.trim() || !f.city_state.trim() || !f.description.trim()) {
      toast.error("Preencha nome da rota, cidade/estado e descrição.");
      return;
    }
    setLoading(true);
    try {
      await suggestRoute({
        name: f.name || null,
        email: f.email || null,
        route_name: f.route_name,
        city_state: f.city_state,
        description: f.description,
      });
      toast.success("Obrigado pela sugestão!", { description: "Nossa equipe vai analisar e, se aprovada, publicaremos em breve." });
      setF({ name: "", email: "", route_name: "", city_state: "", description: "" });
      onClose?.();
    } catch {
      toast.error("Não conseguimos enviar agora. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div
      data-testid="suggest-route-modal"
      className="fixed inset-0 z-[100] flex items-stretch sm:items-center justify-center sm:px-4 sm:py-8"
      role="dialog"
      aria-modal="true"
    >
      {/* Backdrop */}
      <button
        aria-label="Fechar"
        onClick={onClose}
        data-testid="suggest-route-backdrop"
        className="absolute inset-0 bg-black/75 backdrop-blur-md"
      />

      {/* Panel — full-height sheet on mobile, centered card on desktop */}
      <div
        className="relative w-full sm:max-w-xl card-rotta bg-[#111] sm:border sm:border-[#242424] flex flex-col overflow-hidden sm:rounded-2xl rounded-none"
        style={{ maxHeight: "100dvh" }}
      >
        <button
          onClick={onClose}
          data-testid="suggest-route-close"
          className="absolute top-3 right-3 z-10 p-2 text-[#B8B8B8] hover:text-[#C6A762] transition-colors bg-[#111]/80 rounded-full"
          aria-label="Fechar modal"
          style={{ marginTop: "env(safe-area-inset-top)" }}
        >
          <X size={20} />
        </button>

        {/* Scrollable content */}
        <div
          className="flex-1 overflow-y-auto overscroll-contain px-6 sm:px-9 pt-8 sm:pt-9 pb-6"
          style={{ paddingTop: "max(2rem, env(safe-area-inset-top))" }}
        >
          <div className="flex items-center gap-2 text-[#C6A762] mb-4">
            <span className="gold-divider" />
            <span className="text-[10px] tracking-[0.3em] uppercase">Comunidade ROTTA</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-white leading-tight">
            Indique uma <span className="text-gold-gradient">rota</span>
          </h2>
          <p className="text-[#B8B8B8] mt-3 text-sm leading-relaxed">
            Conhece uma estrada que merece estar no ROTTA? Nos conte sobre ela — nossa equipe
            analisa cada sugestão e, se aprovada, publicamos com créditos à comunidade.
          </p>

          <form
            id="suggest-route-form"
            onSubmit={submit}
            className="mt-6 space-y-4"
            data-testid="suggest-route-form"
          >
            <div className="grid sm:grid-cols-2 gap-3">
              <input
                data-testid="suggest-name"
                className="input-rotta"
                placeholder="Seu nome (opcional)"
                value={f.name}
                onChange={set("name")}
              />
              <input
                data-testid="suggest-email"
                type="email"
                className="input-rotta"
                placeholder="Seu e-mail (opcional)"
                value={f.email}
                onChange={set("email")}
              />
            </div>
            <input
              data-testid="suggest-route-name"
              required
              className="input-rotta"
              placeholder="Nome da rota — ex: Estrada Real, trecho de Ouro Preto"
              value={f.route_name}
              onChange={set("route_name")}
            />
            <div className="relative">
              <MapPin size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C6A762]" />
              <input
                data-testid="suggest-city-state"
                required
                className="input-rotta pl-10"
                placeholder="Cidade/Estado — ex: Ouro Preto/MG"
                value={f.city_state}
                onChange={set("city_state")}
              />
            </div>
            <textarea
              data-testid="suggest-description"
              required
              rows={4}
              className="input-rotta resize-none"
              placeholder="Conte um pouco: extensão, tipo de piso, paisagem, paradas obrigatórias…"
              value={f.description}
              onChange={set("description")}
            />
          </form>
        </div>

        {/* Sticky footer with submit button */}
        <div
          className="shrink-0 border-t border-[#1f1f1f] bg-[#111]/95 backdrop-blur px-6 sm:px-9 pt-4 pb-5"
          style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}
        >
          <button
            data-testid="suggest-submit"
            form="suggest-route-form"
            type="submit"
            disabled={loading}
            className="btn-gold w-full py-3.5 rounded-full text-xs inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? "Enviando…" : "Enviar sugestão"} <Send size={14} />
          </button>
          <p className="text-[11px] text-[#7d7d7d] tracking-wide leading-relaxed mt-3 text-center">
            Ao enviar você concorda em compartilhar essas informações com a equipe ROTTA.
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
